import { api } from '../configs/axiosConfigs';
import { BaseResult } from '../../interface/common';
import { GetSportRadarChannelDTO } from '../../interface/liveChat';
import { GetUserFavoritesDTO, SportType } from '../../interface/odds';
import qs from 'qs';
import axios, { AxiosError } from 'axios';
import MD5 from 'crypto-js/md5';
import Hex from 'crypto-js/enc-hex';
import { SettingState } from '../../features';
import { Utils } from '@supersports/styleguide';
import { redirectMaintenance } from '../configs/redirects';

type UserPoolingType = 'Balance' | 'Favorite' | 'MiniBetList' | 'Notification';
export type UserPoolingData = {
  [key in UserPoolingType]: {
    Success: boolean;
    Code: number;
    Message: string;
    Data: any;
  };
};

export const UserAPI = {
  generateMerchantToken: async (account: string) => {
    if (process.env.NODE_ENV === 'development') {
      const timestamp = Date.now().toString();
      const source = account + account + timestamp + '2f04a231-d22c-df12-9101-5ff3c8d4ad9c';
      const hash = MD5(source).toString(Hex).toUpperCase();

      return {
        Token: hash + account + timestamp,
      };
    } else {
      const response = await api.request({
        url: '/api/v1/generate-merchant-token',
        method: 'GET',
        params: {
          account: account,
        },
      });

      return response?.data?.Data;
    }
  },
  authenticate: async (
    MerchantCode: string,
    ProviderCode: string,
    MerchantToken: string,
    UserName: string,
    Currency: string
  ) => {
    try {
      const response = await api.request({
        url: '/api/v1/user/authenticate',
        method: 'GET',
        headers: {
          MerchantCode: MerchantCode,
          ProviderCode: ProviderCode,
        },
        params: {
          MerchantToken: MerchantToken,
          UserName: UserName,
          Currency: Currency,
        },
      });
      return response?.data?.Data;
    } catch (e) {
      redirectMaintenance();
      if (axios.isAxiosError(e)) {
        throw e;
      }
    }
  },
  refreshToken: async (token: string) => {
    try {
      const response = await api.put('/api/v1/user/renew-session', null, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return (response.data as BaseResult).Data as string;
    } catch (error) {
      const data = (error as AxiosError).response?.data as { Message: string } | undefined;
      throw new Error(data?.Message);
    }
  },
  getBalance: async (token: string) => {
    const response = await api.request({
      url: '/api/v1/user/balance',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response?.data?.Data?.Balance;
  },
  getFavorites: async (token: string, FetchDetail = false): Promise<any> => {
    const response = await api.request<BaseResult<GetUserFavoritesDTO>>({
      url: '/api/v1/user/favorites',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        Lang: Utils.convertToLocale(localStorage.getItem('lang') ?? 'en-US'),
        FetchDetail,
      },
    });
    return response?.data?.Data;
  },
  postFavorites: async (token: string, favoriteData: number[], sportType: SportType): Promise<any> => {
    const response = await api.request<BaseResult>({
      url: '/api/v1/user/favorites',
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        MatchIds: favoriteData,
        SportType: sportType,
      },
      paramsSerializer: (params) => {
        // Check if favoriteData is empty
        if (!favoriteData.length) {
          params.MatchIds = ['']; // Exchange empty array with an array of empty string to prevent BE from throwing error
        }
        return qs.stringify(params, { arrayFormat: 'repeat', allowDots: true });
      },
    });
    return response?.data?.Data;
  },
  getProviderSettings: async (token: string) => {
    const response = await api.request<BaseResult>({
      url: '/api/v1/user/settings',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.Data;
  },

  postUserPreferences: async (settings: SettingState, token: string) => {
    const response = await api.request<BaseResult>({
      url: '/api/v1/user/preferences',
      method: 'POST',
      data: {
        ...settings,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.Data;
  },
  getUserPolling: async (token: string, Lang: string): Promise<UserPoolingData> => {
    const response = await api.request<BaseResult<UserPoolingData>>({
      url: '/api/v1/user/polling',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        Lang: Utils.convertToLocale(Lang),
      },
    });

    return response?.data?.Data;
  },
  getSportRadarChannel: async (
    { MatchGroupID }: { MatchGroupID: string },
    token: string
  ): Promise<GetSportRadarChannelDTO> => {
    const response = await api.request<BaseResult<GetSportRadarChannelDTO>>({
      url: '/api/v1/user/channel-create',
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        MatchGroupId: MatchGroupID,
      },
    });

    return response?.data?.Data;
  },
};
